<template>
    <GenericItem
        :labels="getLabels()"
        :actions="getActions()"
        :list-view="listView"
        is-labels-compact>
        <template v-slot:custom>
            <div class="uk-flex uk-flex-center">
                <div
                    v-if="showcase.mediaFileId"
                    :data-src="
                        apiUrl +
                        '/manage-clickandbuys/' +
                        showcase.id +
                        '/media/' +
                        showcase.mediaFileId +
                        '?token=' +
                        getToken
                    "
                    class="item-thumbnail uk-background-cover"
                    uk-img />
                <div v-else class="item-thumbnail item-thumbnail-initials">
                    {{ getInitials(showcase.title) }}
                </div>
            </div>

            <div class="uk-margin-small-top item-title">
                {{ showcase.title }}
            </div>

            <div class="uk-margin-top" />
        </template>

        <template v-if="showcase.fundraising?.goal" v-slot:afterSpacer>
            <div class="uk-margin-small-top" />

            <div class="percentage-text">
                {{ getNumberFormattedNext(getPercentage, 0, 0) + '%' }}
            </div>
            <progress
                :uk-tooltip="
                    'title:' + getNumberFormatted(getPercentage, 0) + '%'
                "
                :value="getPercentage"
                class="uk-progress uk-margin-remove border-radius-next"
                max="100" />
            <div class="uk-margin-small-top" />
            <div class="uk-text-center">
                {{
                    getNumberWithCurrency(
                        showcase.fundraising?.total,
                        false,
                        true
                    )
                }}
                /
                {{ getNumberWithCurrency(showcase.fundraising?.goal) }}
            </div>
        </template>
    </GenericItem>
</template>

<script>
import GenericItem from '@/components/generic/GenericItem';
import UIkit from 'uikit';
import QRCode from 'easyqrcodejs';

export default {
    name: 'ShowcaseItem',
    components: {GenericItem},
    props: {
        showcase: Object,
        listView: Boolean,
        settings: Object,
    },
    data() {
        return {
            loading: false,
            loadingError: null,

            qrInstance: null,
        };
    },
    computed: {
        getPercentage() {
            return this.showcase.fundraising?.goal
                ? (this.showcase.fundraising?.total /
                      this.showcase.fundraising?.goal) *
                      100
                : 0;
        },
    },
    methods: {
        getInitials(name) {
            if (!name) return '?';
            return name
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((w) => w[0].toUpperCase())
                .join('');
        },
        actionGetLink() {
            if (this.settings.clickAndBuyInfos.domain) {
                return (
                    'https://' +
                    this.settings.clickAndBuyInfos.domain +
                    '/' +
                    this.$i18n.locale +
                    '/' +
                    this.showcase.id
                );
            }

            return (
                window.location.origin +
                '/' +
                this.$i18n.locale +
                '/lunacart/' +
                this.showcase.id
            );
        },
        actionDownloadQRCode() {
            const tempEl = document.createElement('div');
            tempEl.style.display = 'none';
            document.body.appendChild(tempEl);

            const qr = new QRCode(tempEl, {
                text: this.actionGetLink(),
                onRenderingEnd: () => {
                    qr.download('lunacart ' + this.showcase.title);
                    document.body.removeChild(tempEl);
                },
            });
        },
        actionLinkOpen() {
            let link = this.actionGetLink();

            event.stopPropagation();
            event.preventDefault();
            window.open(link, '_blank').focus();
        },
        actionLinkToClipboard() {
            let link = this.actionGetLink();

            this.copyToClipboard(link);

            UIkit.notification({
                message: this.$t('copied.to.clipboard', {
                    description: this.$t('link'),
                }),
                status: 'info',
                pos: 'top-center',
                timeout: this.notificationTimeout,
            });
        },
        getLabels() {
            let labels = [];

            labels.push({
                mode: this.showcase.disabled ? 'warning' : 'success',
                icon: this.showcase.disabled ? 'ban' : 'bolt',
                name: this.showcase.disabled
                    ? this.$t('disabled')
                    : this.$t('active'),
            });

            labels.push({
                mode: this.showcase.hidden ? 'default' : 'success',
                icon: this.showcase.hidden ? 'eye-closed' : 'eye',
                name: this.showcase.hidden
                    ? this.$t('not.listed')
                    : this.$t('listed'),
            });

            if (this.showcase.fundraising) {
                labels.push({
                    mode: 'theme',
                    icon: 'star',
                    name: this.$t(this.showcase.fundraising.type),
                });
            }

            labels.push({
                mode: 'default',
                icon: 'tag',
                name:
                    this.showcase.price && this.showcase.price > 0
                        ? this.getNumberWithCurrency(this.showcase.price)
                        : this.$t('free'),
                tooltip: this.$t('price'),
            });

            if (
                this.showcase.variantIds &&
                this.showcase.variantIds.length > 0
            ) {
                labels.push({
                    mode: 'default',
                    icon: 'settings',
                    name: this.showcase.variantIds.length,
                    tooltip: this.$t('variants'),
                });
            }

            if (this.showcase.shippingFee && this.showcase.shippingFee > 0) {
                labels.push({
                    mode: 'default',
                    icon: 'mail',
                    name:
                        this.showcase.shippingFee &&
                        this.showcase.shippingFee > 0
                            ? this.getNumberWithCurrency(
                                  this.showcase.shippingFee
                              )
                            : this.$t('free'),
                    tooltip: this.$t('shipping'),
                });
            }

            if (
                this.showcase.articleFileIds &&
                this.showcase.articleFileIds.length > 0
            ) {
                labels.push({
                    mode: 'default',
                    icon: 'file',
                    name: this.showcase.articleFileIds.length,
                    tooltip: this.$t(
                        this.showcase.articleFileIds.length === 1
                            ? 'file'
                            : 'files'
                    ),
                });
            }

            if (
                this.showcase.attachedFileIds &&
                this.showcase.attachedFileIds.length > 0
            ) {
                labels.push({
                    mode: 'default',
                    icon: 'download',
                    name: this.showcase.attachedFileIds.length,
                    tooltip: this.$t(
                        this.showcase.attachedFileIds.length === 1
                            ? 'attachment'
                            : 'attachments'
                    ),
                });
            }

            return labels;
        },
        getActions() {
            let actions = [];

            actions.push({
                name: this.$t('edit'),
                mode: 'theme',
                icon: 'settings',
                function: this.actionEdit,
            });

            actions.push({
                name: this.$t('open'),
                mode: 'default',
                icon: 'link',
                function: this.actionLinkOpen,
            });
            actions.push({
                name: this.$t('qr'),
                mode: 'default',
                icon: 'download',
                function: this.actionDownloadQRCode,
            });

            return actions;
        },
        actionEdit() {
            this.$emit('select', this.showcase);
        },
    },
};
</script>

<style lang="scss" scoped>
.item-thumbnail {
    height: 100px;
    width: 100px;
    border-radius: $border-radius-next;
}

.item-title {
    font-size: 17px;
    font-weight: 500;
}

.item-thumbnail-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #14183b;
    color: #fff;
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 0.05em;
    border-radius: $border-radius-next;
    user-select: none;
}

.percentage-text {
    font-size: 15px;
    margin-bottom: 3px;
}
</style>
